/**
 * History Container Component
 * Container component with business logic for history page
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useChatHistoryStore, SearchEntry } from '@/store/chatHistoryStore';
import { useHistorySearch } from '@/hooks/useHistorySearch';
import { useConversationHistory } from '@/hooks/useConversationHistory';
import { generateUserId } from '@/services/chatService';
import { deleteConversation } from '@/services/chatHistoryService';
import { format } from 'date-fns';
import { ConversationItem } from '@/types/chatTypes';
import { HistoryPresenter } from './HistoryPresenter';
import { SharePopup } from '@/components/common/SharePopup';
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { useShare } from '@/hooks/useShare';

export const HistoryContainer: React.FC = () => {
  const router = useRouter();
  const { searchHistory } = useChatHistoryStore();
  const [userId, setUserId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<{
    index: number;
    conversationId: string;
  } | null>(null);
  const {
    openSharePopup,
    shareUrl,
    popupOpen: sharePopupOpen,
    setPopupOpen: setSharePopupOpen,
    shareTitle,
    shareDescription,
  } = useShare({
    title: 'Share Chat',
    description: 'Copy the link below to share this conversation with others.',
  });

  // Get or generate user_id on mount
  useEffect(() => {
    // Only run on client side (localStorage is not available on server)
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const generatedUserId = generateUserId();
      setUserId(generatedUserId);
    } catch (err) {
      console.error('❌ [HistoryContainer] Failed to generate user ID:', err);
    }
  }, []);

  // Use React Query hook with no caching
  const {
    conversationHistory,
    isLoading: isApiLoading,
    error: apiError,
  } = useConversationHistory(userId);

  // React Query client for invalidating cache
  const queryClient = useQueryClient();

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async ({
      userId,
      conversationId,
    }: {
      userId: string;
      conversationId: string;
    }) => {
      return deleteConversation(userId, conversationId);
    },
    onSuccess: () => {
      // Invalidate and refetch conversation history after successful delete
      queryClient.invalidateQueries({
        queryKey: ['conversationHistory', userId],
      });
    },
    onError: error => {
      console.error('❌ [HistoryContainer] Delete error:', error);
      // You could add a toast notification here if needed
    },
  });

  // Log API state for debugging
  useEffect(() => {
    if (conversationHistory) {
      console.log(
        '📋 [HistoryContainer] Conversation history from API:',
        conversationHistory
      );
      console.log(
        '📋 [HistoryContainer] Conversations count:',
        conversationHistory.conversations?.length || 0
      );
      console.log(
        '📋 [HistoryContainer] Conversations data:',
        conversationHistory.conversations
      );
    }
    if (apiError) {
      console.error('❌ [HistoryContainer] API Error:', apiError);
    }
    if (isApiLoading) {
      console.log('⏳ [HistoryContainer] Loading conversation history...');
    }
  }, [conversationHistory, apiError, isApiLoading]);

  /**
   * Converts API conversation items to SearchEntry format
   */
  const convertConversationsToSearchEntries = useMemo((): SearchEntry[] => {
    if (!conversationHistory?.conversations) {
      console.log(
        '⚠️ [HistoryContainer] No conversations in history:',
        conversationHistory
      );
      return [];
    }

    console.log(
      '🔄 [HistoryContainer] Converting conversations to search entries:',
      conversationHistory.conversations.length,
      'items'
    );

    const entries = conversationHistory.conversations.map(
      (item: ConversationItem) => {
        // Convert timestamp to Date (API returns milliseconds)
        const date = new Date(item.last_update_time);
        const formattedDate = format(date, 'yyyy-MM-dd');
        const formattedTime = format(date, 'MMM d, yyyy');

        const entry = {
          footer: formattedDate,
          input: item.title || `Conversation ${item.id.substring(0, 8)}...`,
          title: item.title || `Conversation ${item.id.substring(0, 8)}...`,
          aiReply: item.description || formattedTime, // Use description from API, fallback to formatted time
          displayType: 'text' as const,
          isTravelQuery: false,
          canGenerateItinerary: false,
          missingDetails: null,
          travelDetails: null,
          travelItems: [],
          quickFacts: [],
          // Store conversation ID and timestamp for future use
          conversationId: item.id,
          last_update_time: item.last_update_time,
          // Also store as lastUpdateTime (camelCase) for compatibility
          lastUpdateTime: item.last_update_time,
        } as SearchEntry & {
          conversationId: string;
          last_update_time: number;
          lastUpdateTime: number;
        };

        console.log('✅ [HistoryContainer] Converted entry:', {
          id: item.id,
          title: entry.title,
          conversationId: entry.conversationId,
        });

        return entry;
      }
    );

    console.log(
      '✅ [HistoryContainer] Converted entries count:',
      entries.length
    );

    return entries;
  }, [conversationHistory]);

  // Merge API history with local history and sort by timestamp (newest first)
  const mergedHistory = useMemo(() => {
    const apiEntries = convertConversationsToSearchEntries;
    const localEntries = searchHistory;

    console.log('🔄 [HistoryContainer] Merging history:', {
      apiEntriesCount: apiEntries.length,
      localEntriesCount: localEntries.length,
      totalCount: apiEntries.length + localEntries.length,
    });

    // Combine API and local entries
    const merged = [...apiEntries, ...localEntries];

    // Sort all entries by timestamp (newest first)
    // Use last_update_time for API entries and timestamp for local entries
    const sortedMerged = merged.sort((a, b) => {
      // Get timestamp for entry A
      const aTimestamp =
        (a as SearchEntry & { last_update_time?: number })?.last_update_time ||
        (a as SearchEntry & { timestamp?: number })?.timestamp ||
        0;

      // Get timestamp for entry B
      const bTimestamp =
        (b as SearchEntry & { last_update_time?: number })?.last_update_time ||
        (b as SearchEntry & { timestamp?: number })?.timestamp ||
        0;

      return bTimestamp - aTimestamp; // Newest first (descending order)
    });

    console.log('✅ [HistoryContainer] Sorted merged history:', {
      totalCount: sortedMerged.length,
      sortedTimestamps: sortedMerged.map(entry => {
        const timestamp =
          (entry as SearchEntry & { last_update_time?: number })
            ?.last_update_time ||
          (entry as SearchEntry & { timestamp?: number })?.timestamp ||
          0;
        return {
          title: entry.title,
          timestamp: timestamp
            ? new Date(timestamp).toISOString()
            : 'no-timestamp',
        };
      }),
    });

    return sortedMerged;
  }, [convertConversationsToSearchEntries, searchHistory]);

  const historyToUse = mergedHistory;

  const { searchQuery, setSearchQuery, filteredHistory } = useHistorySearch({
    history: historyToUse,
  });

  // Debug: Log filtered history
  useEffect(() => {
    console.log('🔍 [HistoryContainer] Filtered history:', {
      totalCount: filteredHistory.length,
      searchQuery,
      entries: filteredHistory.map(e => ({
        id: (e as SearchEntry & { conversationId?: string })?.conversationId,
        title: e.title,
      })),
    });
  }, [filteredHistory, searchQuery]);

  const handleNewChat = () => {
    router.push('/chat?new=1');
  };

  const handleLogoClick = () => {
    // Navigate to chat to show full landing page
    router.push('/chat?view=full-landing');
  };

  const handleEntryClick = (index: number) => {
    const entry = filteredHistory[index];
    const conversationId = (entry as SearchEntry & { conversationId?: string })
      ?.conversationId;

    if (conversationId) {
      // Navigate to chat with conversationId to load messages from API
      router.push({
        pathname: '/chat',
        query: { conversationId },
      });
    } else {
      // Fallback: if no conversationId, use the old behavior
      router.push({
        pathname: '/chat',
        query: { historyId: index },
      });
    }
  };

  const handleShare = async (index: number) => {
    const entry = filteredHistory[index];
    const conversationId = (entry as SearchEntry & { conversationId?: string })
      ?.conversationId;

    // Get the base URL - use production domain for sharing (matching activity-details og:url pattern)
    const getBaseUrl = () => {
      if (typeof window === 'undefined') {
        return 'https://nexgo.ai';
      }
      // Use production domain for sharing (consistent with activity-details, package-details, etc.)
      // This ensures shared links always point to the production domain
      return window.location.origin;
    };

    // Construct the shareable chat link
    const baseUrl = getBaseUrl();
    const url = conversationId
      ? `${baseUrl}/chat?conversationId=${conversationId}`
      : `${baseUrl}/chat`;

    // Open the share popup with the custom URL
    await openSharePopup(url);
  };

  const handleDelete = (index: number) => {
    const entry = filteredHistory[index];
    const conversationId = (entry as SearchEntry & { conversationId?: string })
      ?.conversationId;

    if (!conversationId || !userId) {
      console.error(
        '❌ [HistoryContainer] Cannot delete: missing conversationId or userId'
      );
      return;
    }

    // Store conversation to delete and open confirmation dialog
    setConversationToDelete({ index, conversationId });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!conversationToDelete || !userId) {
      console.error(
        '❌ [HistoryContainer] Cannot delete: missing conversation data'
      );
      setDeleteDialogOpen(false);
      return;
    }

    try {
      await deleteMutation.mutateAsync({
        userId,
        conversationId: conversationToDelete.conversationId,
      });
      // Close dialog after successful delete
      setDeleteDialogOpen(false);
      setConversationToDelete(null);
    } catch (error) {
      console.error(
        '❌ [HistoryContainer] Failed to delete conversation:',
        error
      );
      // Keep dialog open on error so user can retry or cancel
      // You could add a toast notification here if needed
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setConversationToDelete(null);
  };

  // Debug: Log props being passed to presenter
  useEffect(() => {
    console.log('🎯 [HistoryContainer] Props to HistoryPresenter:', {
      filteredHistoryCount: filteredHistory.length,
      isLoading: isApiLoading,
      searchQuery,
      hasEntries: filteredHistory.length > 0,
      firstEntry: filteredHistory[0]
        ? {
            title: filteredHistory[0].title,
            conversationId: (
              filteredHistory[0] as SearchEntry & { conversationId?: string }
            )?.conversationId,
          }
        : null,
    });
  }, [filteredHistory, isApiLoading, searchQuery]);

  return (
    <>
      <HistoryPresenter
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filteredHistory={filteredHistory}
        isLoading={isApiLoading}
        onNewChat={handleNewChat}
        onLogoClick={handleLogoClick}
        onEntryClick={handleEntryClick}
        onShare={handleShare}
        onDelete={handleDelete}
      />
      <SharePopup
        open={sharePopupOpen}
        onOpenChange={setSharePopupOpen}
        shareUrl={shareUrl}
        title={shareTitle}
        description={shareDescription}
      />
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title='Delete Conversation'
        description='Are you sure you want to delete this conversation? This action cannot be undone.'
        confirmLabel='Delete'
        cancelLabel='Cancel'
        confirmVariant='destructive'
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};
